import PropTypes from 'prop-types';
import React, { createRef } from 'react';
import ColorSelector from '../../color_selector.jsx';
import classnames from 'classnames';
import _ from 'underscore';
import DynamicTextArea from '../../dynamic_text_area.jsx';
import utils from '../../../utils';

const trim = function(str) {
    return str.trim();
};
const empty = function(str) {
    return str.length;
};

class Mine extends React.Component {
    static displayName = 'Mine';

    static propTypes = {
        isQuestion: PropTypes.bool,
        canAskQuestion: PropTypes.bool,
        update: PropTypes.func.isRequired,
        deleteMarkup: PropTypes.func.isRequired,
        close: PropTypes.func.isRequired,
        highlight: PropTypes.any.isRequired,
        hideSharingQA: PropTypes.bool.isRequired,
    };

    constructor(props) {
        super(props);
        const { isQuestion, canAskQuestion } = props;
        const note_text = props.highlight.note_text;

        this.state = {
            disableQuestion: !note_text || note_text.length === 0,
            isQuestion,
            canAskQuestion,
            saving: false,
        };
    }

    hasUnmounted = false;
    // There's a debounced 'setState' call below

    tags = createRef();
    isQuestion = createRef();

    noteTextRef = el => {
        this.noteText = el;
    };

    componentDidMount() {
        this.focusOnInput();
    }

    componentWillUnmount() {
        this.hasUnmounted = true;
    }

    onChangeNoteText = event => {
        const disableQuestion = event.target.value.length === 0;
        if (this.state.disableQuestion !== disableQuestion) {
            this.setState({
                disableQuestion: disableQuestion,
            });
        }
        this.scheduleUpdate();
    };

    onChangeQuestion = e => {
        this.setState({
            isQuestion: e.target.checked,
        });
        // add slight delay to what to update note
        this.updateNote();
    };

    onDelete = e => {
        const { deleteMarkup, highlight } = this.props;
        e.preventDefault();
        e.stopPropagation();

        deleteMarkup(highlight);
        // destroying the highlight will close the popover.
    };

    setSavingStatus = data => {
        const { note_text, is_question } = data;

        let text = note_text == '' ? 'Highlight saved' : 'Note saved';
        if (is_question) {
            text = 'Question saved';
        }

        this.setState({
            saving: text,
        });
    };

    cancelSubmit = e => {
        e.preventDefault();
        e.stopPropagation();
    };

    changeColor = color => {
        this.updateNote(color);
    };

    close = () => {
        this.props.close();
    };

    focusOnInput = () => {
        // need to find and set the scroll position, otherwise the viewport will jump
        const x = window.scrollX;
        const y = window.scrollY;
        this.noteText.focus();
        window.scrollTo(x, y);
    };

    resetSavingStatus = () => {
        !this.hasUnmounted &&
            this.setState({
                saving: false,
            });
    };

    // creates a debounce function to prevent multiple submissions to api
    // this is useful for instituting auto-save for text inputs

    scheduleUpdate = _.debounce(() => this.updateNote(), 500);

    updateNote = (color = this.props.highlight.color) => {
        const { update } = this.props;
        const data = {
            color: color,
            note_text: this.noteText.value,
            tags: this.tags.current.value
                .split(',')
                .map(trim)
                .filter(empty),
        };

        if (this.state.canAskQuestion && this.isQuestion.current) {
            data.is_question = this.isQuestion.current.checked;
        }

        this.setSavingStatus(data);

        update(this.props.highlight, data);

        _.delay(this.resetSavingStatus, 1500);
    };

    renderHeader = () => {
        const color = this.props.highlight.color;

        return (
            <header>
                <ColorSelector color={color} onChange={this.changeColor} />
                {this.renderSaving()}
                <button onClick={this.close} className="close">
                    ×
                </button>
            </header>
        );
    };

    renderQuestion = () => {
        if (this.state.canAskQuestion && !this.props.hideSharingQA) {
            if (!this.questionId) {
                this.questionId = _.uniqueId('q');
            }
            const labelClasses = classnames({
                disabled: this.state.disableQuestion,
            });
            let isQuestion = this.state.isQuestion;
            if (this.state.disableQuestion) {
                isQuestion = false;
            }

            return (
                <fieldset className="is-question-container">
                    <input
                        type="checkbox"
                        id={this.questionId}
                        name="is_question"
                        className="post-question"
                        disabled={this.state.disableQuestion}
                        checked={isQuestion}
                        onChange={this.onChangeQuestion}
                        ref={this.isQuestion}
                    />
                    <label className={labelClasses} htmlFor={this.questionId}>
                        Would you like to post this as a question?
                    </label>
                </fieldset>
            );
        }
    };

    renderSaving = () => {
        if (this.state.saving != false) {
            return (
                <span className="status">
                    <i className="cl-icon cl-icon-check" aria-hidden />{' '}
                    {this.state.saving}
                </span>
            );
        }
        return;
    };

    render() {
        if (!this.tagEntryId) {
            this.tagEntryId = _.uniqueId('tag-entry');
        }
        const note_text = this.props.highlight.note_text;
        let tags = '';
        const tagList = this.props.highlight.tags;
        if (tagList && tagList.length > 0) {
            tags = tagList.join(', ');
        }

        return (
            <div className="highlight-popover">
                {this.renderHeader()}
                <div data-container="note" className="note-content">
                    <form onSubmit={this.cancelSubmit}>
                        <div className="js-note-text" />
                        <fieldset>
                            <DynamicTextArea
                                forwardedRef={this.noteTextRef}
                                defaultValue={note_text}
                                placeholder="Enter text"
                                onChange={this.onChangeNoteText}
                            />
                        </fieldset>
                        <fieldset>
                            <label
                                className="aural"
                                htmlFor={this.tagEntryId}
                                id={this.tagEntryId + '-label'}
                            >
                                Optional field: Enter tags separated by commas
                            </label>
                            <input
                                aria-describedby={this.tagEntryId + '-label'}
                                className="dynamic-textarea"
                                defaultValue={tags}
                                id={this.tagEntryId}
                                name="tags"
                                onChange={this.scheduleUpdate}
                                placeholder="Tags (comma separated)"
                                ref={this.tags}
                                type="text"
                            />
                        </fieldset>
                        {this.renderQuestion()}
                        <a
                            onClick={this.onDelete}
                            role="button"
                            onKeyDown={utils.ifSpace(this.onDelete)}
                            className="btn btn-small delete"
                            data-action="delete"
                        >
                            <i className="cl-icon cl-icon-trash-o" /> Delete
                        </a>
                    </form>
                </div>
            </div>
        );
    }
}

export default Mine;



// WEBPACK FOOTER //
// ./src/components/text_viewer/highlight/mine.jsx